import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { cardBorderClassName } from "../../lib/cardStyles";
import {
  loadOnboarding,
  saveOnboarding,
} from "../../lib/onboarding";

const API_BASE =
  import.meta.env.VITE_API_URL || "https://app.neurospherevoiceai.com/api";

const PHONE_FORWARDING_HINT =
  "Forward your existing business line to this number to put your receptionist to work. Forwarding instructions were included in your activation email.";

type AvailableNumber = {
  phone_number: string;
  friendly_name?: string;
};

function areaCodeFromOnboardingPhone(phone?: string): string {
  const digits = (phone ?? "").replace(/\D/g, "");
  let nanp = digits;
  if (nanp.length === 11 && nanp.startsWith("1")) {
    nanp = nanp.slice(1);
  } else if (nanp.length > 10 && nanp.startsWith("1")) {
    nanp = nanp.slice(1);
  }
  if (nanp.length < 3) return "";
  const npa = nanp.slice(0, 3);
  if (!/^[2-9]\d{2}$/.test(npa)) return "";
  return npa;
}

function formatPhoneDisplay(phone: string): string {
  const d = phone.replace(/\D/g, "");
  const normalized = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  if (normalized.length === 10) {
    return `+1 (${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
  }
  return phone;
}

export default function ChooseNumber() {
  const navigate = useNavigate();
  const ob = useMemo(() => loadOnboarding(), []);
  const step2Phone = String(ob?.step2?.phone ?? "");

  const [areaCode, setAreaCode] = useState(() =>
    areaCodeFromOnboardingPhone(step2Phone),
  );
  const [numbers, setNumbers] = useState<AvailableNumber[]>([]);
  const [selected, setSelected] = useState<AvailableNumber | null>(null);
  const [searching, setSearching] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [error, setError] = useState("");
  const [emptyHint, setEmptyHint] = useState(false);
  const [assignedNumber, setAssignedNumber] = useState<string | null>(null);
  const [alreadyAssigned, setAlreadyAssigned] = useState(false);

  function handleAuthStatus(status: number): boolean {
    if (status === 401 || status === 403) {
      navigate("/login");
      return true;
    }
    return false;
  }

  async function fetchAssignedDid(): Promise<string | null> {
    const customerId =
      localStorage.getItem("customerId") ||
      (ob?.customerId != null ? String(ob.customerId) : "");
    if (!customerId) return null;
    try {
      const res = await fetch(`${API_BASE}/customers/${customerId}/phone-settings`, {
        credentials: "include",
      });
      if (!res.ok) return null;
      const data = (await res.json().catch(() => ({}))) as {
        twilio_phone_number?: string;
        phone_number?: string;
      };
      const raw = String(data.twilio_phone_number || data.phone_number || "").trim();
      return raw || null;
    } catch {
      return null;
    }
  }

  async function showAlreadyAssigned(knownNumber?: string | null) {
    const did = (knownNumber || "").trim() || (await fetchAssignedDid());
    setAlreadyAssigned(true);
    setAssignedNumber(did);
    setNumbers([]);
    setSelected(null);
    setEmptyHint(false);
    setError("");
    if (did && ob) {
      saveOnboarding({ ...ob, chosenPhoneNumber: did });
    }
  }

  async function searchNumbers() {
    const code = areaCode.replace(/\D/g, "").slice(0, 3);
    if (!/^\d{3}$/.test(code)) {
      setError("Enter a 3-digit area code.");
      setEmptyHint(false);
      return;
    }
    setSearching(true);
    setError("");
    setEmptyHint(false);
    setSelected(null);
    setNumbers([]);
    try {
      const res = await fetch(
        `${API_BASE}/customers/me/available-numbers?area_code=${encodeURIComponent(code)}`,
        { credentials: "include" },
      );
      const data = (await res.json().catch(() => ({}))) as {
        numbers?: AvailableNumber[];
        error?: string;
        phone_number?: string;
      };
      if (handleAuthStatus(res.status)) return;
      if (res.status === 409 && /already/i.test(String(data.error || ""))) {
        await showAlreadyAssigned(data.phone_number);
        return;
      }
      if (res.status === 429) {
        setError("Try again in a moment.");
        return;
      }
      if (res.status === 502) {
        setError(
          String(data.error || "Could not search numbers. Please try again."),
        );
        return;
      }
      if (!res.ok) {
        setError(String(data.error || `Could not search numbers (${res.status}).`));
        return;
      }
      const list = (data.numbers || []).slice(0, 10);
      setNumbers(list);
      if (list.length === 0) {
        setEmptyHint(true);
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSearching(false);
    }
  }

  async function provisionSelected() {
    if (!selected?.phone_number) return;
    setProvisioning(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/customers/me/provision-number`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone_number: selected.phone_number }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        phone_number?: string;
        error?: string;
      };
      if (handleAuthStatus(res.status)) return;
      if (res.status === 409 && /already/i.test(String(data.error || ""))) {
        await showAlreadyAssigned(data.phone_number);
        return;
      }
      if (res.status === 429) {
        setError("Try again in a moment.");
        return;
      }
      if (res.status === 502) {
        setError(
          String(
            data.error ||
              "We couldn't assign that number. Please try another or contact support.",
          ),
        );
        return;
      }
      if (!res.ok) {
        setError(String(data.error || `Could not assign that number (${res.status}).`));
        return;
      }
      const live = String(data.phone_number || selected.phone_number);
      if (ob) {
        saveOnboarding({ ...ob, chosenPhoneNumber: live });
      }
      setAssignedNumber(live);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setProvisioning(false);
    }
  }

  if (assignedNumber || alreadyAssigned) {
    const display = assignedNumber
      ? formatPhoneDisplay(assignedNumber)
      : "A number is already assigned to this account.";
    return (
      <PickerFrame>
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mb-8 mx-auto"
          style={{
            background: "rgba(34, 197, 94, 0.12)",
            border: "2px solid rgba(34, 197, 94, 0.5)",
            boxShadow: "0 0 40px rgba(34, 197, 94, 0.3)",
          }}
        >
          <i
            className="ri-phone-line"
            style={{ fontSize: "36px", color: "rgb(74, 222, 128)" }}
          />
        </div>
        <h1
          className="text-4xl md:text-5xl font-bold mb-4 text-center"
          style={{
            fontFamily: "'Orbitron', sans-serif",
            background: "linear-gradient(135deg, #8a2be2 0%, #ff6a00 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Your number is live.
        </h1>
        <p
          className="text-2xl text-white text-center font-semibold mb-3"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {display}
        </p>
        {assignedNumber ? (
          <p
            className="text-lg text-gray-300 text-center mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Call it now.
          </p>
        ) : null}
        <p
          className="text-sm text-gray-400 text-center max-w-md mb-10 leading-relaxed"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {PHONE_FORWARDING_HINT}
        </p>
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="px-8 py-4 rounded-xl text-base font-bold text-white cursor-pointer"
          style={{
            background: "linear-gradient(90deg, #8a2be2, #ff6a00)",
            fontFamily: "'Space Grotesk', sans-serif",
            boxShadow: "0 0 32px rgba(138, 43, 226, 0.4)",
          }}
        >
          Continue to login
        </button>
      </PickerFrame>
    );
  }

  return (
    <PickerFrame>
      <h1
        className="text-4xl md:text-5xl font-bold mb-3 text-center"
        style={{
          fontFamily: "'Orbitron', sans-serif",
          background: "linear-gradient(135deg, #8a2be2 0%, #ff6a00 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Choose your number
      </h1>
      <p
        className="text-base text-gray-300 text-center max-w-lg mb-8 leading-relaxed"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        Search by area code, pick a number, and confirm. This becomes the
        dedicated line for your receptionist.
      </p>

      <div
        className={`w-full max-w-md rounded-xl p-5 mb-6 ${cardBorderClassName}`}
        style={{ background: "rgba(13, 13, 18, 0.7)" }}
      >
        <label
          htmlFor="area-code"
          className="block text-xs uppercase tracking-widest text-gray-500 mb-3"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Area code
        </label>
        <div className="flex gap-3">
          <input
            id="area-code"
            inputMode="numeric"
            maxLength={3}
            value={areaCode}
            onChange={(e) =>
              setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))
            }
            placeholder="949"
            className="w-28 rounded-xl border border-white/10 bg-slate-950/50 px-4 py-3 text-center text-lg tracking-widest text-white outline-none focus:border-white/25"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          />
          <button
            type="button"
            onClick={searchNumbers}
            disabled={searching || areaCode.length !== 3}
            className="flex-1 rounded-xl text-base font-bold text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "linear-gradient(90deg, #8a2be2, #ff6a00)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            {searching ? "Searching…" : "Search"}
          </button>
        </div>
      </div>

      {error ? (
        <p
          className="w-full max-w-md mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/50 text-red-300 text-sm text-center"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {error}
        </p>
      ) : null}

      {emptyHint ? (
        <p
          className="w-full max-w-md mb-4 text-sm text-gray-300 text-center"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          No numbers in that area code — try a nearby code.
        </p>
      ) : null}

      {numbers.length > 0 ? (
        <ul className="w-full max-w-md space-y-2 mb-6">
          {numbers.map((n) => {
            const active = selected?.phone_number === n.phone_number;
            const label = n.friendly_name || formatPhoneDisplay(n.phone_number);
            return (
              <li key={n.phone_number}>
                <button
                  type="button"
                  onClick={() => setSelected(n)}
                  className={`w-full text-left rounded-xl px-4 py-3 cursor-pointer ${cardBorderClassName} ${
                    active ? "border-[#8a2be2]/60 bg-white/10" : "bg-white/5"
                  }`}
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  <div className="text-white font-semibold">{label}</div>
                  <div className="text-xs text-gray-500">{n.phone_number}</div>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {selected ? (
        <div className="w-full max-w-md text-center">
          <p
            className="text-sm text-gray-400 mb-4"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Assign {selected.friendly_name || formatPhoneDisplay(selected.phone_number)} to
            this account?
          </p>
          <button
            type="button"
            onClick={provisionSelected}
            disabled={provisioning}
            className="px-8 py-4 rounded-xl text-base font-bold text-white cursor-pointer disabled:opacity-50"
            style={{
              background: "linear-gradient(90deg, #8a2be2, #ff6a00)",
              fontFamily: "'Space Grotesk', sans-serif",
              boxShadow: "0 0 32px rgba(138, 43, 226, 0.4)",
            }}
          >
            {provisioning ? "Assigning…" : "Confirm"}
          </button>
        </div>
      ) : null}
    </PickerFrame>
  );
}

function PickerFrame({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen w-full bg-transparent text-white">
      <header className="border-b border-white/10 bg-slate-950/70 backdrop-blur sticky top-0 z-50">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate("/home")}
          >
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-500 to-orange-400" />
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-wide">NeuroSphere</div>
              <div className="text-xs text-white/60">Onboarding</div>
            </div>
          </div>
        </div>
      </header>
      <main className="min-h-[calc(100vh-73px)] w-full flex flex-col items-center justify-center px-6 py-16">
        {children}
      </main>
    </div>
  );
}
